import { useCallback, useEffect, useMemo, useState } from 'react'
import { isSameIstDay } from '@/lib/ist'
import { sumPaise } from '@/lib/money'
import type { CommitDeliveryPayload } from '../../commands'
import { bagWeight } from '../../model/delivery'
import { useLotFigures } from '../../pages/delivery/hooks'
import { Avatar, Btn, Cta, Footer, Icon, Keypad, Readout, V1Screen, V1Sheet, WaitCta } from '../kit'
import { C, speak, typeKey } from '../style'
import { V1Photo } from '../photo'
import type { V1IconName } from '../sprite'
import {
  tr,
  fmtDate,
  kg,
  money,
  plain,
  price,
  rosterShort,
  shortCode,
  shortLot,
  amountWords,
  sentences,
} from '../text'
import { Money, Rich } from '../Rich'
import { StepHeader } from './shared'
import { useV1 } from './ctx'

function useTotals() {
  const lots = useLotFigures()
  const total = sumPaise(lots.map((l) => l.figures.amount_paise))
  const bags = lots.reduce((s, l) => s + l.plan.bag_count, 0)
  const net = lots.reduce((s, l) => s + l.figures.net_g, 0)
  return { lots, total, bags, net }
}

const answer = (a: string | null) => (a ? tr(`gate.v1.answer.${a}` as 'gate.v1.answer.NO') : '—')

function Line({
  label,
  value,
  strong,
  big,
}: {
  label: string
  value: React.ReactNode
  strong?: boolean
  big?: boolean
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: 8,
        fontWeight: strong ? 700 : undefined,
      }}
    >
      <span style={{ fontSize: 17 }}>{label}</span>
      <span style={{ fontSize: big ? 24 : 18, whiteSpace: 'nowrap' }}>{value}</span>
    </div>
  )
}

function InfoLine({ icon, children }: { icon: V1IconName; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
      <span
        aria-hidden="true"
        style={{
          width: 36,
          height: 36,
          flexShrink: 0,
          borderRadius: 10,
          background: C.sunk,
          color: C.acc,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon name={icon} size={22} />
      </span>
      <span style={{ fontSize: 17, lineHeight: 1.3, flexGrow: 1 }}>{children}</span>
    </div>
  )
}

/**
 * F4.8 — आपका हिसाब: the phone turned to the farmer. Price and amount appear here first; he
 * agrees (that is also his yes to the account). Hindi only, read aloud on सुनें.
 */
export function FarmerSlipScreen({
  onBack,
  onAgreed,
}: {
  onBack: () => void
  onAgreed: () => void
}) {
  const { d, dispatch, session, stamp } = useV1()
  const { lots, total } = useTotals()
  const [open, setOpen] = useState<Record<string, boolean>>({})
  const words = amountWords(total)
  const parts = lots.length
  const verified = d.farmer.bank_verified && d.farmer.account_last4
  /** V1: checked at the gate (or in the office) and waiting for the office's approval. */
  const pending = !verified ? (d.farmer.bank_pending_last4 ?? null) : null

  const read = () =>
    speak(
      sentences(
        [
          `${tr('gate.v1.slip.value')} ${words}`,
          verified ? plain(tr('gate.v1.slip.account', { last4: d.farmer.account_last4! })) : '',
          parts > 1 ? plain(tr('gate.v1.slip.parts', { count: parts })) : '',
          tr('gate.v1.slip.bank'),
          tr('gate.v1.slip.otp'),
        ].filter(Boolean),
      ),
    )

  return (
    <V1Screen
      main={{ gap: 8 }}
      header={
        <>
          <div
            style={{
              background: C.help,
              color: '#ffffff',
              padding: '8px 12px',
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Icon name="showfarmer" size={28} />
              <span style={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <b style={{ fontSize: 18, lineHeight: 1.2 }}>{tr('gate.v1.slip.title')}</b>
              </span>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
              <button
                type="button"
                onClick={onBack}
                style={{
                  height: 40,
                  padding: '0 12px',
                  borderRadius: 20,
                  border: '1.5px solid #ffffff',
                  background: 'transparent',
                  color: '#ffffff',
                  fontFamily: "'Mukta', sans-serif",
                  fontSize: 15,
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  flexShrink: 0,
                  cursor: 'pointer',
                }}
              >
                <Icon name="back" size={18} />
                {tr('gate.v1.slip.handBack')}
              </button>
              <button
                type="button"
                onClick={read}
                style={{
                  height: 40,
                  padding: '0 14px',
                  borderRadius: 20,
                  border: 0,
                  background: '#ffffff',
                  color: C.help,
                  fontFamily: "'Mukta', sans-serif",
                  fontSize: 15,
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  cursor: 'pointer',
                }}
              >
                <Icon name="speaker" size={20} />
                {tr('gate.v1.listen')}
              </button>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px' }}>
            <Avatar url={d.farmer.photo_url} size={48} icon={41} />
            <span style={{ display: 'flex', flexDirection: 'column' }}>
              <b style={{ fontSize: 20, lineHeight: 1.2 }}>{d.farmer.name}</b>
              <span style={{ fontSize: 15, color: C.muted }}>
                {tr('gate.v1.farmerNo', { no: rosterShort(d.farmer.roster_no) })} ·{' '}
                {session.village_name} · {fmtDate(d.started_at)}
              </span>
            </span>
          </div>
        </>
      }
      footer={
        <Footer>
          <Cta
            label={tr('gate.v1.slip.agrees')}
            icon="ok"
            onClick={() => {
              dispatch({ type: 'farmer_agrees', at: stamp(), account_confirmed: !!verified })
              onAgreed()
            }}
          />
        </Footer>
      }
    >
      <div
        style={{
          borderRadius: 12,
          border: `2.5px solid ${C.ink}`,
          background: C.surface,
          padding: '8px 12px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <span style={{ fontSize: 18 }}>{tr('gate.v1.slip.value')}</span>
          <b style={{ fontSize: 40, lineHeight: 1.1 }}>
            <Money paise={total} small={0.55} />
          </b>
        </div>
        <span style={{ fontSize: 16, color: C.muted }}>{words}</span>
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '8px 10px',
          borderRadius: 12,
          background: C.surface,
          border: `1.5px solid ${C.rule}`,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 44,
            height: 44,
            flexShrink: 0,
            borderRadius: 10,
            background: verified ? C.okWash : pending ? C.warnWash : C.badWash,
            color: verified ? C.ok : pending ? C.accDark : C.bad,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon name="passbook" size={26} />
        </span>
        <div style={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <span style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.25 }}>
            {verified
              ? tr('gate.v1.slip.account', { last4: d.farmer.account_last4! })
              : pending
                ? tr(
                    d.farmer.bank_queued
                      ? 'gate.v1.slip.queuedAccount'
                      : 'gate.v1.slip.pendingAccount',
                    { last4: pending },
                  )
                : tr('gate.v1.slip.noAccount')}
          </span>
          {verified && (
            <span style={{ fontSize: 16, lineHeight: 1.3 }}>{tr('gate.v1.slip.isYours')}</span>
          )}
        </div>
      </div>
      {parts > 1 && (
        <InfoLine icon="hourglass">
          <Rich text={tr('gate.v1.slip.parts', { count: parts })} />
        </InfoLine>
      )}
      <InfoLine icon="rupee">{tr('gate.v1.slip.bank')}</InfoLine>
      <InfoLine icon="call">{tr('gate.v1.slip.otp')}</InfoLine>
      <section
        style={{
          background: C.surface,
          border: `1.5px solid ${C.rule}`,
          borderRadius: 12,
          padding: '8px 12px',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Line label={tr('gate.v1.storage.q1short')} value={answer(d.storage.stored_over_7_days)} />
        <Line label={tr('gate.v1.storage.q2short')} value={answer(d.storage.tablet_or_powder)} />
      </section>
      {lots.map(({ plan, bags, figures, card }) => {
        const isRed = plan.stream === 'CONV'
        const shown = !!open[plan.id]
        return (
          <section
            key={plan.id}
            style={{
              background: C.surface,
              border: `2px solid ${isRed ? C.bad : C.ok}`,
              borderRadius: 12,
              padding: '8px 10px',
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Icon
                name={isRed ? 'redtie' : 'sack'}
                size={28}
                style={{ color: isRed ? C.bad : C.ok }}
              />
              <span
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  flexGrow: 1,
                  minWidth: 0,
                  lineHeight: 1.2,
                }}
              >
                <b style={{ fontSize: 17 }}>
                  {tr(isRed ? 'gate.v1.slip.redLot' : 'gate.v1.slip.goodLot', {
                    count: plan.bag_count,
                  })}
                </b>
                <b style={{ fontSize: 22 }}>
                  <Money paise={figures.amount_paise} />
                </b>
              </span>
              <button
                type="button"
                aria-expanded={shown}
                onClick={() => setOpen((o) => ({ ...o, [plan.id]: !shown }))}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  minHeight: 44,
                  padding: '0 8px',
                  borderRadius: 10,
                  border: `1.5px solid ${C.border}`,
                  background: C.surface,
                  color: C.ink,
                  fontFamily: "'Mukta', sans-serif",
                  fontSize: 15,
                  fontWeight: 700,
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                }}
              >
                {tr(shown ? 'gate.v1.slip.hide' : 'gate.v1.slip.show')}
                <Icon
                  name="down"
                  size={18}
                  style={{ transform: shown ? 'rotate(180deg)' : undefined }}
                />
              </button>
            </div>
            {shown && (
              <>
                {bags
                  .filter((b) => !b.withdrawn)
                  .map((b) => (
                    <div
                      key={b.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '4px 8px',
                        borderRadius: 8,
                        background: C.sunk,
                        fontSize: 16,
                      }}
                    >
                      <span>
                        {tr('gate.v1.slip.lot', { code: shortLot(plan.code) })} ·{' '}
                        {tr('gate.v1.slip.bagLine', { tag: shortCode(b.qr_payload) })}
                      </span>
                      <b>{tr('gate.v1.kg', { kg: kg(bagWeight(b)?.gross_g ?? 0) })}</b>
                    </div>
                  ))}
                <Line
                  label={tr('gate.v1.slip.sack')}
                  value={tr('gate.v1.slip.less', { kg: kg(figures.tare_g) })}
                />
                <Line
                  label={tr('gate.v1.slip.net')}
                  value={tr('gate.v1.kg', { kg: kg(figures.net_g) })}
                  strong
                />
                {card?.price_paise_per_kg != null && (
                  <Line
                    label={tr('gate.v1.slip.price')}
                    value={tr('gate.v1.slip.priceLine', {
                      kg: price(card.price_paise_per_kg),
                      q: price(card.price_paise_per_kg * 100),
                    })}
                  />
                )}
                <Line
                  label={tr('gate.v1.slip.amount')}
                  value={<Money paise={figures.amount_paise} />}
                  strong
                  big
                />
              </>
            )}
          </section>
        )
      })}
    </V1Screen>
  )
}

/** The next printed slip number in order: today's highest + 1, kept to its width ("004127"). */
function useNextSlipNo(): string | null {
  const { outbox, d } = useV1()
  return useMemo(() => {
    let best: string | null = null
    for (const r of outbox) {
      if (r.type !== 'commit_delivery' || !isSameIstDay(r.created_at, Date.parse(d.started_at)))
        continue
      const no = (r.payload as CommitDeliveryPayload).slip.slip_no
      if (!best || Number(no) > Number(best)) best = no
    }
    if (!best) return null
    return String(Number(best) + 1).padStart(best.length, '0')
  }, [outbox, d.started_at])
}

/** Slip numbers already written today on this phone. */
function useUsedSlips(): Set<string> {
  const { outbox, d } = useV1()
  return useMemo(() => {
    const s = new Set<string>()
    for (const r of outbox) {
      if (r.type !== 'commit_delivery' || !isSameIstDay(r.created_at, Date.parse(d.started_at)))
        continue
      s.add((r.payload as CommitDeliveryPayload).slip.slip_no.replace(/^0+/, ''))
    }
    return s
  }, [outbox, d.started_at])
}

/** F4.8 — पर्ची लिखें: copy it onto the printed slip, his thumb on the copy, a photo. */
export function PaperSlipScreen({ onBack, onNext }: { onBack: () => void; onNext: () => void }) {
  const { d, dispatch } = useV1()
  const { total, bags, net } = useTotals()
  const suggested = useNextSlipNo()
  const used = useUsedSlips()
  const [typing, setTyping] = useState(false)
  const slipNo = d.slip.slip_no ?? ''
  const dup = slipNo !== '' && used.has(slipNo.replace(/^0+/, ''))

  useEffect(() => {
    if (!d.slip.slip_no && suggested) dispatch({ type: 'slip', patch: { slip_no: suggested } })
  }, [d.slip.slip_no, suggested, dispatch])

  const rows: [string, string][] = [
    [tr('gate.v1.paperSlip.farmer'), `${rosterShort(d.farmer.roster_no)} · ${d.farmer.name}`],
    [tr('gate.v1.paperSlip.bags'), String(bags)],
    [tr('gate.v1.paperSlip.kg'), kg(net)],
    [tr('gate.v1.paperSlip.amount'), money(total)],
    [
      tr('gate.v1.paperSlip.answers'),
      `${answer(d.storage.stored_over_7_days)} · ${answer(d.storage.tablet_or_powder)}`,
    ],
  ]
  const ready = /^\d+$/.test(slipNo) && !dup && !!d.slip.photo_media_id

  return (
    <V1Screen
      main={{ padding: '8px 14px', gap: 10 }}
      header={
        <StepHeader step="slip" icon="slip" title={tr('gate.v1.paperSlip.title')} onBack={onBack} />
      }
      footer={
        <Footer>
          {ready ? (
            <Cta label={tr('gate.v1.paperSlip.next')} icon="next" onClick={onNext} />
          ) : (
            <WaitCta>
              {!slipNo
                ? tr('gate.v1.paperSlip.typeNo')
                : dup
                  ? tr('gate.v1.paperSlip.used')
                  : tr('gate.v1.final.left', { name: tr('gate.v1.final.slipPhoto') })}
            </WaitCta>
          )}
        </Footer>
      }
      overlay={
        typing ? (
          <SlipNoSheet
            value={slipNo}
            onClose={() => setTyping(false)}
            onSave={(no) => {
              dispatch({ type: 'slip', patch: { slip_no: no } })
              setTyping(false)
            }}
          />
        ) : null
      }
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '4px 10px',
          borderRadius: 12,
          background: C.surface,
          border: `1.5px solid ${dup ? C.bad : C.rule}`,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 44,
            height: 44,
            flexShrink: 0,
            borderRadius: 10,
            background: C.sunk,
            color: C.acc,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon name="eye" size={26} />
        </span>
        <div style={{ flexGrow: 1, minWidth: 0 }}>
          <span style={{ fontSize: 19, fontWeight: 700, lineHeight: 1.25 }}>
            {slipNo
              ? tr('gate.v1.paperSlip.number', { no: slipNo })
              : tr('gate.v1.paperSlip.typeNo')}
          </span>
          {dup && (
            <div style={{ fontSize: 15, color: C.bad, fontWeight: 700 }}>
              {tr('gate.v1.paperSlip.used')}
            </div>
          )}
        </div>
        <div style={{ flex: '0 0 auto', display: 'flex' }}>
          <Btn icon="pencil" onClick={() => setTyping(true)}>
            {tr('gate.v1.change')}
          </Btn>
        </div>
      </div>
      <section
        style={{
          background: C.surface,
          border: `2px solid ${C.acc}`,
          borderRadius: 12,
          padding: '8px 12px',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: C.acc }}>
          <span
            aria-hidden="true"
            style={{
              width: 26,
              height: 26,
              flexShrink: 0,
              borderRadius: 13,
              border: `2px solid ${C.ink}`,
              color: C.ink,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 15,
              fontWeight: 700,
              lineHeight: 1,
              boxSizing: 'border-box',
            }}
          >
            1
          </span>
          <Icon name="slip" size={22} />
        </span>
        {rows.map(([k, v]) => (
          <div
            key={k}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              gap: 10,
              padding: '1px 0',
              borderBottom: `1px solid ${C.rule}`,
            }}
          >
            <span style={{ fontSize: 16, color: C.muted }}>{k}</span>
            <b style={{ fontSize: 18, textAlign: 'right', lineHeight: 1.3 }}>{v}</b>
          </div>
        ))}
      </section>
      <V1Photo
        kind="document"
        value={d.slip.photo_media_id}
        onChange={(id) => dispatch({ type: 'slip', patch: { photo_media_id: id } })}
        render={({ preview, busy, take }) => {
          const has = !!d.slip.photo_media_id
          return (
            <div
              style={{
                borderRadius: 12,
                border: `2px solid ${has ? C.ok : C.acc}`,
                background: C.surface,
                padding: 6,
                display: 'flex',
                gap: 10,
                alignItems: 'center',
              }}
            >
              <span
                role="img"
                aria-label={tr('gate.v1.paperSlip.thumb')}
                style={{
                  width: 76,
                  height: 56,
                  borderRadius: 8,
                  background: C.photo,
                  color: C.photoInk,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  overflow: 'hidden',
                }}
              >
                {preview ? (
                  <img
                    src={preview}
                    alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <Icon name="camera" size={30} sw={1.6} />
                )}
              </span>
              <span style={{ display: 'flex', flexDirection: 'column', gap: 4, flexGrow: 1 }}>
                <span style={{ fontSize: 16, lineHeight: 1.3 }}>
                  {tr('gate.v1.paperSlip.thumb')}
                </span>
                {has && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '3px 10px 3px 4px',
                      borderRadius: 16,
                      background: C.okWash,
                      color: C.ok,
                      fontWeight: 700,
                      fontSize: 16,
                      lineHeight: 1.25,
                      alignSelf: 'flex-start',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: 11,
                        background: C.ok,
                        color: '#ffffff',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Icon name="ok" size={15} sw={3} />
                    </span>
                    {tr('gate.v1.paperSlip.photoTaken')}
                  </span>
                )}
              </span>
              <div style={{ flex: '0 0 auto', display: 'flex' }}>
                <Btn icon="camera" onClick={take} disabled={busy}>
                  {tr(has ? 'gate.v1.paperSlip.again' : 'gate.v1.paperSlip.takePhoto')}
                </Btn>
              </div>
            </div>
          )
        }}
      />
    </V1Screen>
  )
}

function SlipNoSheet({
  value,
  onClose,
  onSave,
}: {
  value: string
  onClose: () => void
  onSave: (no: string) => void
}) {
  const [v, setV] = useState(value)
  const onKey = useCallback((k: string) => setV((c) => typeKey(c, k, { decimals: 0, max: 8 })), [])
  return (
    <V1Sheet
      title={tr('gate.v1.paperSlip.typeNo')}
      onClose={onClose}
      top={120}
      actions={
        v ? (
          <Cta label={tr('gate.v1.save')} icon="ok" onClick={() => onSave(v)} />
        ) : (
          <WaitCta>{tr('gate.v1.paperSlip.typeNo')}</WaitCta>
        )
      }
    >
      <Readout label={tr('gate.v1.paper.slipNo')} value={v} size={32} />
      <div style={{ flexGrow: 1 }} />
      <Keypad dot={false} onKey={onKey} />
    </V1Sheet>
  )
}
